import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { QuickViewModalShellComponent } from '../quick-view-modal-shell/quick-view-modal-shell.component';

@Component({
  selector: 'lib-devices-profile-modal-template',
  standalone: true,
  imports: [CommonModule, QuickViewModalShellComponent],
  template: `
    <lib-quick-view-modal-shell
      [isOpen]="true"
      [title]="title"
      [canClose]="canCancel"
      [showCloseButton]="showHeaderClose"
      [canSubmit]="keyboardCanSubmit"
      [autofocus]="autofocus"
      (close)="close.emit()"
      (submit)="onKeyboardSubmit()"
    >
        @if (useDefaultLayout) {
          <form class="quick-view-form">
            <label>
              ID
              <input type="text" [value]="id" readonly data-modal-primary-focus="true" />
            </label>
            @if (showCompanyId) {
              <label>
                Company
                <input type="text" [value]="companyDisplay" readonly />
              </label>
            }
            @if (showClinic) {
              <label>
                Clinic
                <input type="text" [value]="clinicName" readonly />
                <div class="quick-view-id-meta">{{ clinicId }}</div>
              </label>
            }
            <label>
              Device Type
              <input type="text" [value]="deviceTypeName" readonly />
              <div class="quick-view-id-meta">{{ deviceTypeId }}</div>
            </label>
            @if (showSerialNumber) {
              <label>
                Serial Number
                <input type="text" [value]="serialNumber" readonly />
              </label>
            }
            <label>
              Brand
              <input type="text" [value]="brand" readonly />
            </label>
            <label>
              Model
              <input type="text" [value]="model" readonly />
            </label>
            @if (showStatus) {
              <label>
                Status
                <input type="text" [value]="status" readonly />
              </label>
            }
            @if (showCreatedAt) {
              <label>
                Created
                <input type="text" [value]="createdAtText" readonly />
              </label>
            }
            @if (showUpdatedAt) {
              <label>
                Updated
                <input type="text" [value]="updatedAtText" readonly />
              </label>
            }
            @if (showManufactureDate) {
              <label>
                Manufacture Date (text)
                <input type="text" [value]="manufactureDateText" readonly />
              </label>
            }
            @if (showLastServiceDate) {
              <label>
                Last Service Date (text)
                <input type="text" [value]="lastServiceDateText" readonly />
              </label>
            }
            @if (showAddedDate) {
              <label>
                Added Date (text)
                <input type="text" [value]="addedDateText" readonly />
              </label>
            }
            @if (showLifecycle) {
              <label>
                Lifecycle Events
                <input type="text" [value]="lifecycleEventsText" readonly />
              </label>
              <label>
                Latest Lifecycle Event
                <input type="text" [value]="latestLifecycleEventText" readonly />
              </label>
            }
            @if (showNotes) {
              <label class="quick-view-form__wide">
                Notes
                <textarea [value]="notes" readonly></textarea>
              </label>
            }
            @if (showPhotos) {
              <label class="quick-view-form__wide">
                Photos
                <input type="text" [value]="photosText" readonly />
                @if (photosPreview.length > 0) {
                  <div class="devices-profile-modal__photo-grid">
                    @for (photo of photosPreview; track photo.url) {
                      <a
                        class="devices-profile-modal__photo-link"
                        [href]="photo.url"
                        target="_blank"
                        rel="noreferrer"
                        [title]="photo.name || photo.key || 'Device photo'"
                      >
                        <img
                          [src]="photo.url"
                          [alt]="photo.name || 'Device photo'"
                          class="devices-profile-modal__photo-image"
                        />
                      </a>
                    }
                  </div>
                }
              </label>
            }

            @if (hasFooterActions) {
              <div class="quick-view-actions">
                @if (hasActionOne) {
                  <button
                    type="button"
                    class="quick-view-action"
                    [disabled]="actionOneDisabled"
                    (click)="onActionOne()"
                  >
                    {{ actionOneLabel }}
                  </button>
                }
                @if (hasActionTwo) {
                  <button
                    type="button"
                    class="quick-view-action"
                    [disabled]="actionTwoDisabled"
                    (click)="onActionTwo()"
                  >
                    {{ actionTwoLabel }}
                  </button>
                }
                @if (hasPrimaryAction) {
                  <button
                    type="button"
                    class="quick-view-action"
                    [disabled]="primaryActionDisabled"
                    (click)="onPrimaryAction()"
                  >
                    {{ primaryActionLabel }}
                  </button>
                }
              </div>
            }
          </form>
        } @else {
          <ng-content></ng-content>
        }
    </lib-quick-view-modal-shell>
  `,
  styles: [`
    .devices-profile-modal__photo-grid {
      margin-top: 4px;
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
      gap: 8px;
    }

    .devices-profile-modal__photo-link {
      display: block;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      overflow: hidden;
      background: #f8fafc;
    }

    .devices-profile-modal__photo-image {
      display: block;
      width: 100%;
      height: 96px;
      object-fit: cover;
      background: #e5e7eb;
    }
  `],
})
export class DevicesProfileModalTemplateComponent {
  @Input() public title = 'Device profile';
  @Input() public closeText = 'Close';
  @Input() public useDefaultLayout = true;
  @Input() public showHeaderClose = true;
  @Input() public canCancel = true;
  @Input() public canSubmit = false;
  @Input() public autofocus = true;
  @Input() public keyboardSubmitMode: 'none' | 'primary' | 'submit' = 'none';
  @Input() public primaryActionLabel = '';
  @Input() public primaryActionDisabled = false;
  @Input() public actionOneLabel = '';
  @Input() public actionOneDisabled = false;
  @Input() public actionTwoLabel = '';
  @Input() public actionTwoDisabled = false;
  @Input() public showCompanyId = false;

  @Input() public id = '';
  @Input() public companyDisplay = '—';
  @Input() public companyId = '';
  @Input() public clinicName = '';
  @Input() public clinicId = '';
  @Input() public showClinic = true;
  @Input() public deviceTypeName = '';
  @Input() public deviceTypeId = '';
  @Input() public serialNumber = '—';
  @Input() public showSerialNumber = true;
  @Input() public brand = '—';
  @Input() public model = '—';
  @Input() public status: string | null = '—';
  @Input() public showStatus = true;
  @Input() public createdAtText = '—';
  @Input() public showCreatedAt = true;
  @Input() public updatedAtText = '—';
  @Input() public showUpdatedAt = true;
  @Input() public manufactureDateText = '—';
  @Input() public showManufactureDate = true;
  @Input() public lastServiceDateText = '—';
  @Input() public showLastServiceDate = true;
  @Input() public addedDateText = '—';
  @Input() public showAddedDate = true;
  @Input() public notes = '—';
  @Input() public showNotes = true;
  @Input() public photosText = '0';
  @Input() public photosPreview: Array<{ key?: string; url: string; name?: string }> = [];
  @Input() public showPhotos = true;
  @Input() public lifecycleEventsText = '0';
  @Input() public latestLifecycleEventText = '—';
  @Input() public showLifecycle = true;

  @Output() public readonly close = new EventEmitter<void>();
  @Output() public readonly primaryAction = new EventEmitter<void>();
  @Output() public readonly actionOne = new EventEmitter<void>();
  @Output() public readonly actionTwo = new EventEmitter<void>();
  @Output() public readonly submit = new EventEmitter<void>();

  protected get hasPrimaryAction(): boolean {
    return this.primaryActionLabel.trim().length > 0;
  }

  protected get hasActionOne(): boolean {
    return this.actionOneLabel.trim().length > 0;
  }

  protected get hasActionTwo(): boolean {
    return this.actionTwoLabel.trim().length > 0;
  }

  protected get hasFooterActions(): boolean {
    return this.hasPrimaryAction || this.hasActionOne || this.hasActionTwo;
  }

  protected get keyboardCanSubmit(): boolean {
    if (this.keyboardSubmitMode === 'submit') {
      return this.canSubmit;
    }

    if (this.keyboardSubmitMode === 'primary') {
      return this.hasPrimaryAction && !this.primaryActionDisabled;
    }

    return false;
  }

  protected onPrimaryAction(): void {
    if (!this.hasPrimaryAction || this.primaryActionDisabled) {
      return;
    }
    this.primaryAction.emit();
  }

  protected onActionOne(): void {
    if (!this.hasActionOne || this.actionOneDisabled) {
      return;
    }
    this.actionOne.emit();
  }

  protected onActionTwo(): void {
    if (!this.hasActionTwo || this.actionTwoDisabled) {
      return;
    }
    this.actionTwo.emit();
  }

  protected onKeyboardSubmit(): void {
    if (this.keyboardSubmitMode === 'primary') {
      this.onPrimaryAction();
      return;
    }

    if (this.keyboardSubmitMode === 'submit' && this.canSubmit) {
      this.submit.emit();
    }
  }
}
